import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  CircularProgress,
  Link as JoyLink,
  Sheet,
} from '@mui/joy';
import { Link } from 'react-router';
import TrendingUp from '@mui/icons-material/TrendingUp';
import { getSubscriptionStatusLabel } from '../utils';
import { GET_MY_SUBSCRIPTION } from '../gql/subscription.queries';

const GET_CLIENT_CREDITS = gql`
  query GetClient {
    getClient {
      _id
      credits
      totalCredits
      periodCreditsUsed
      currentTier {
        _id
        name
        monthlyCredits
        maxCreditsAllowed
      }
      subscriptionStatus
    }
  }
`;

const ClientCredits = () => {
  const { data, loading } = useQuery(GET_CLIENT_CREDITS, { pollInterval: 10000 });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION, { pollInterval: 60000 });
  // console.log('ClientCredits data:', data);
  const client = data?.getClient;
  const credits = client?.credits || 0;
  const tierName = client?.currentTier?.name || 'Free';
  const subscriptionStatus = client?.subscriptionStatus;
  const cancelAtPeriodEnd = subscriptionData?.mySubscription?.cancelAtPeriodEnd;
  // Calculate progress percentage
  const usedCredits = client?.periodCreditsUsed || 0;
  const totalCredits = client?.totalCredits || 0;
  const progressValue = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;

  const getColor = () => {
    if (credits <= 10) return 'danger';
    if (credits <= 30) return 'warning';
    return 'success';
  };

  const getStatusColor = () => {
    switch (subscriptionStatus) {
      case 'active': return 'success';
      case 'trialing': return 'primary';
      case 'past_due': return 'warning';
      case 'canceled': return 'danger';
      default: return 'neutral';
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Card
      id="onboarding-billing"
      variant="outlined"
      sx={{
        width: '100%',
        maxWidth: "100%",
        mb: 1,
        borderRadius: 'lg',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 0.5 }}>
        <Stack spacing={1.5}>
          {/* Tier Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp sx={{ fontSize: 16, color: 'primary.500' }} />
              <Typography level="title-sm" fontWeight="lg">
                {tierName}
              </Typography>
            </Box>
            <Chip
              size="sm"
              color={getStatusColor()}
              variant="soft"
              sx={{ fontWeight: 500 }}
            >
              {getSubscriptionStatusLabel(subscriptionStatus, cancelAtPeriodEnd)}
            </Chip>
          </Box>

          {/* Credits Display */}
          <Sheet
            variant="soft"
            color={getColor()}
            sx={{
              p: 1.5,
              borderRadius: 'md',
              textAlign: 'center',
            }}
          >
            <Typography level="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {credits}
            </Typography>
            <Typography level="body-xs" sx={{ mt: 0.5 }}>
              credits remaining
            </Typography>
          </Sheet>

          {/* Progress Bar */}
          <Box>
            <LinearProgress
              determinate
              value={Math.min(progressValue, 100)}
              color={getColor()}
              size="sm"
              sx={{
                // height: 6,
                borderRadius: 'xl',
                // bgcolor: 'background.level2'
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography level="body-xs" color="neutral">
                Used: {usedCredits}
              </Typography>
              <Typography level="body-xs" color="neutral">
                Total: {totalCredits}
              </Typography>
            </Box>
          </Box>

          {/* Action Link */}
          <JoyLink
            level="body-xs"
            sx={{
              textAlign: 'center',
              cursor: 'pointer',
              fontWeight: 500,
              py: 0,
              mb: 0,
            }}
            to="/settings/billing"
            component={Link}
          >
            Manage Billing →
          </JoyLink>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ClientCredits;
